import {
  MutableRefObject,
  type ReactNode,
  createContext,
  createElement,
  useCallback,
  useContext,
  useState,
  useSyncExternalStore,
} from 'react';
import { useStore } from 'react-redux';
import type { FormManager, FormManagerFactory, SubmitHandler } from './formBuilder';

export interface FormProviderProps<Input, SubmitPayload, AdditionalSubmitArg = void, Manager = unknown> {
  input: Input;
  onSubmit: SubmitHandler<SubmitPayload, AdditionalSubmitArg, Manager>;
  children: ReactNode;
  managerRef?: MutableRefObject<null | Manager>;
}

export interface FieldObject<FieldValue> {
  value: FieldValue;
  onValueChange(newValue: FieldValue): void;
  onBlur(): void;
  onChange(e: { target: { value: FieldValue } }): void;
  disabled: boolean;
  error: boolean;
  helperText: string | undefined;
}

export interface FormHooks<
  State,
  StoreState extends Record<string, unknown>,
  Input,
  SingleFieldNames extends string,
  SingleFields extends Record<SingleFieldNames, never>,
  MultiFieldNames extends string,
  MultiFields extends Record<MultiFieldNames, { key: string | number; value: never }>,
  ActionNames extends string,
  Actions extends Record<ActionNames, never>,
  SubmitPayload,
  AdditionalSubmitArg,
  Manager = FormManager<
    State,
    StoreState,
    Input,
    SingleFieldNames,
    SingleFields,
    MultiFieldNames,
    MultiFields,
    ActionNames,
    Actions,
    AdditionalSubmitArg
  >,
> {
  FormProvider: React.ComponentType<FormProviderProps<Input, SubmitPayload, AdditionalSubmitArg, Manager>>;
  InternalFormProvider: React.ComponentType<{
    children: ReactNode;
    manager: Manager;
  }>;
  context: React.Context<Manager | undefined>;
  useField<FieldName extends SingleFieldNames>(name: FieldName): FieldObject<SingleFields[FieldName]>;
  useMultiFields<FieldName extends MultiFieldNames>(
    name: FieldName,
    key: MultiFields[FieldName]['key'],
  ): FieldObject<MultiFields[FieldName]['value']>;
  useIsFormValid(): boolean;
  useIsDirty(): boolean;
  useSelector<Result>(selector: (args: { state: State; store: StoreState }) => Result): Result;
  useManager(): Manager;
}

export const createFormHooks = <
  State,
  StoreState extends Record<string, unknown>,
  Input,
  SingleFieldNames extends string,
  SingleFields extends Record<SingleFieldNames, never>,
  MultiFieldNames extends string,
  MultiFields extends Record<MultiFieldNames, { key: string | number; value: never }>,
  ActionNames extends string,
  Actions extends Record<ActionNames, never>,
  SubmitPayload,
  AdditionalSubmitArg = void,
>(
  managerFactory: FormManagerFactory<
    State,
    StoreState,
    Input,
    SingleFieldNames,
    SingleFields,
    MultiFieldNames,
    MultiFields,
    ActionNames,
    Actions,
    SubmitPayload
  >,
  _types?: { additionalSubmitArg: AdditionalSubmitArg },
): FormHooks<
  State,
  StoreState,
  Input,
  SingleFieldNames,
  SingleFields,
  MultiFieldNames,
  MultiFields,
  ActionNames,
  Actions,
  SubmitPayload,
  AdditionalSubmitArg
> => {
  type Manager = FormManager<
    State,
    StoreState,
    Input,
    SingleFieldNames,
    SingleFields,
    MultiFieldNames,
    MultiFields,
    ActionNames,
    Actions,
    AdditionalSubmitArg
  >;
  const context = createContext<Manager | undefined>(undefined);
  const useManager = () => {
    const manager = useContext(context);
    if (!manager) {
      throw new Error('Trying to use a form manager outside of a form context');
    }
    return manager;
  };
  return {
    context,
    FormProvider: ({
      input,
      children,
      onSubmit,
      managerRef,
    }: FormProviderProps<Input, SubmitPayload, AdditionalSubmitArg, Manager>) => {
      const store = useStore<StoreState>();
      const [manager] = useState(() => managerFactory({ input, onSubmit, store }));
      if (managerRef) {
        managerRef.current = manager;
      }
      return createElement(context.Provider, { value: manager }, children);
    },
    InternalFormProvider: ({ manager, children }) => {
      return createElement(context.Provider, { value: manager }, children);
    },
    useField(name) {
      const manager = useManager();
      return {
        onChange: useCallback(
          (e) => {
            manager.onFieldChange(name, e.target.value);
          },
          [manager, name],
        ),
        onValueChange: useCallback(
          (value) => {
            manager.onFieldChange(name, value);
          },
          [manager, name],
        ),
        onBlur: useCallback(() => {
          manager.onFieldBlur(name);
        }, [manager, name]),
        disabled: useSyncExternalStore(manager.subscribe, () => manager.getFieldDisabled(name)),
        value: useSyncExternalStore(manager.subscribe, () => manager.getFieldValue(name)),
        error: useSyncExternalStore(manager.subscribe, () => manager.getFieldError(name)),
        helperText: useSyncExternalStore(manager.subscribe, () => manager.getFieldHelperText(name)),
      };
    },
    useMultiFields(name, key) {
      const manager = useManager();
      return {
        onChange: useCallback(
          (e) => {
            manager.onMultiFieldChange(name, key, e.target.value);
          },
          [manager, name],
        ),
        onValueChange: useCallback(
          (value) => {
            manager.onMultiFieldChange(name, key, value);
          },
          [manager, name],
        ),
        onBlur: useCallback(() => {
          manager.onMultiFieldBlur(name, key);
        }, [manager, name]),
        disabled: useSyncExternalStore(manager.subscribe, () => manager.getMultiFieldDisabled(name, key)),
        value: useSyncExternalStore(manager.subscribe, () => manager.getMultiFieldValue(name, key)),
        error: useSyncExternalStore(manager.subscribe, () => manager.getMultiFieldError(name, key)),
        helperText: useSyncExternalStore(manager.subscribe, () => manager.getMultiFieldHelperText(name, key)),
      };
    },
    useIsFormValid() {
      const manager = useManager();
      return useSyncExternalStore(manager.subscribe, () => manager.isFormValid());
    },
    useIsDirty() {
      const manager = useManager();
      return useSyncExternalStore(manager.subscribe, () => manager.isFormDirty());
    },
    useSelector(selector) {
      const manager = useManager();
      return useSyncExternalStore(manager.subscribe, () => manager.runSelector(selector));
    },
    useManager,
  };
};
